import { useState } from "react";
import apiClient from '../services/apiClient';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import StakeTable, { Column } from './GameEngine/StakeTable';
import { Modal, FormInput, Button } from '../components/ui';

interface Collaborator {
  id: number;
  name: string;
  email: string;
  password2: string;
  balance: string;
  created: string;
  parent_id: number;
  parent_name?: string;
  parent_email?: string;
  referalcode: string;
  profile?: any;
}

interface Manager {
  id: number;
  name: string;
  email: string;
}

const PendingCollaboratorUser = () => {

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    role: "admin",
    amount: "0"
  });

  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [profileModalVisible, setProfileModalVisible] = useState(false);
  const [selectedProfileDetails, setSelectedProfileDetails] = useState<any>(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<"success" | "error">("error");

  // Per-row selected manager ID state: { [affiliateId]: managerId }
  const [selectedManagers, setSelectedManagers] = useState<Record<number, string>>({});

  // Per-row assign status
  const [assignStatus, setAssignStatus] = useState<Record<number, { loading: boolean; message: string; type: "success" | "error" }>>({});

  // Per-row approve status
  const [approveStatus, setApproveStatus] = useState<Record<number, { loading: boolean; message: string; type: "success" | "error" }>>({});

  const queryClient = useQueryClient();
  

  const { data: managers = [] } = useQuery({
    queryKey: ['managerListDropdown'],
    queryFn: async () => {
      const token = localStorage.getItem("token");
      const res = await apiClient.get(`/collaborator/managers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.data?.data || [];
    }
  });

  const { data: myProfile } = useQuery({
    queryKey: ['myProfile'],
    queryFn: async () => {
      const token = localStorage.getItem("token");
      const res = await apiClient.get(`/admin/getMineProfile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.data?.data;
    }
  });


  const { data: adminsData = { data: [], total: 0 }, isLoading: isLoadingAdmins } = useQuery({
    queryKey: ['pendingAffiliates', page, limit, searchTerm],
    queryFn: async () => {
      const token = localStorage.getItem("token");
      const offset = (page - 1) * limit;
      let url = `/collaborator/list?role=affiliate&status=pending&limit=${limit}&offset=${offset}`;
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }
      const res = await apiClient.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return {
        data: res.data?.data || [],
        total: res.data?.total || 0
      };
    }
  });

  const admins = adminsData.data;
  const total = adminsData.total;
  const totalPages = Math.ceil(total / limit);

  const fetchProfileDetails = async (id: number) => {
    try {
      setLoadingProfile(true);
      setProfileModalVisible(true);
      setSelectedProfileDetails(null);

      const token = localStorage.getItem("token");
      const response = await apiClient.get(
        `/collaborator/getProfileDetailsCollaboratorChild/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setSelectedProfileDetails(response.data.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleAssignManager = async (affiliateId: number) => {
    const managerId = selectedManagers[affiliateId];
    if (!managerId) {
      setAssignStatus(prev => ({
        ...prev,
        [affiliateId]: { loading: false, message: "Please select a manager first.", type: "error" }
      }));
      return;
    }

    setAssignStatus(prev => ({
      ...prev,
      [affiliateId]: { loading: true, message: "", type: "success" }
    }));

    try {
      const token = localStorage.getItem("token");
      const response = await apiClient.put(
        `/collaborator/assignManager/${affiliateId}`,
        { manager_id: Number(managerId) },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setAssignStatus(prev => ({
        ...prev,
        [affiliateId]: { loading: false, message: response.data.message || "Assigned!", type: "success" }
      }));

      // Refresh list after assignment
      queryClient.invalidateQueries({ queryKey: ['pendingAffiliates'] });

    } catch (error: any) {
      setAssignStatus(prev => ({
        ...prev,
        [affiliateId]: {
          loading: false,
          message: error?.response?.data?.message || "Failed to assign.",
          type: "error"
        }
      }));
    }
  };

  const handleApproveAffiliate = async (affiliateId: number) => {
    setApproveStatus(prev => ({
      ...prev,
      [affiliateId]: { loading: true, message: "", type: "success" }
    }));

    try {
      const token = localStorage.getItem("token");
      const response = await apiClient.put(
        `/collaborator/approve/${affiliateId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setApproveStatus(prev => ({
        ...prev,
        [affiliateId]: { loading: false, message: response.data.message || "Approved!", type: "success" }
      }));

      // Refresh list
      queryClient.invalidateQueries({ queryKey: ['pendingAffiliates'] });

    } catch (error: any) {
      setApproveStatus(prev => ({
        ...prev,
        [affiliateId]: {
          loading: false,
          message: error?.response?.data?.message || "Failed to approve.",
          type: "error"
        }
      }));
    }
  };

  const handleRejectAffiliate = async (affiliateId: number) => {
    if (!window.confirm("Are you sure you want to reject and delete this affiliate applicant?")) {
      return;
    }
    setApproveStatus(prev => ({
      ...prev,
      [affiliateId]: { loading: true, message: "", type: "success" }
    }));

    try {
      const token = localStorage.getItem("token");
      const response = await apiClient.put(
        `/collaborator/reject/${affiliateId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setApproveStatus(prev => ({
        ...prev,
        [affiliateId]: { loading: false, message: response.data.message || "Rejected!", type: "success" }
      }));

      // Refresh list
      queryClient.invalidateQueries({ queryKey: ['pendingAffiliates'] });

    } catch (error: any) {
      setApproveStatus(prev => ({
        ...prev,
        [affiliateId]: {
          loading: false,
          message: error?.response?.data?.message || "Failed to reject.",
          type: "error"
        }
      }));
    }
  };

  const createMutation = useMutation({
    mutationFn: async (newAffiliate: any) => {
      const token = localStorage.getItem("token");
      return apiClient.post(`/collaborator/create`, newAffiliate, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    onSuccess: (response) => {
      setMessage(response.data.message);
      setMessageType("success");
      queryClient.invalidateQueries({ queryKey: ['pendingAffiliates'] });
      setTimeout(() => setShowModal(false), 1500);
    },
    onError: (error: any) => {
      setMessage(error?.response?.data?.message || "Failed");
      setMessageType("error");
    },
    onSettled: () => setLoading(false)
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    createMutation.mutate(formData);
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const columns: Column[] = [
    { key: 'sn', label: 'S.No.', render: (_, __, i) => (page - 1) * limit + i + 1 },
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'referalcode', label: 'Referral Code' },
    { key: 'ip', label: 'IP Address', render: (_, item: Collaborator) => item.profile?.ip || "-" },
    { key: 'geo', label: 'Geo Location', render: (_, item: Collaborator) => {
        const geo = item.profile?.geo;
        if (!geo || (!geo.city && !geo.country)) return "-";
        return [geo.city, geo.state || geo.region, geo.country].filter(Boolean).join(", ");
      }
    },
    { key: 'created', label: 'Date', render: (val) => new Date(val).toLocaleDateString() },
    { key: 'assign', label: 'Assign Manager', render: (_, item: Collaborator) => {
      if (item.parent_name) {
        return (
          <span className="text-sm font-semibold text-indigo-400">
            {item.parent_name} {item.parent_email ? `(${item.parent_email})` : ''}
          </span>
        );
      }
      return (
        <div className="flex flex-col gap-1 min-w-[180px]">
          <div className="flex gap-2 items-center">
            <select
              value={selectedManagers[item.id] || ""}
              onChange={(e) => setSelectedManagers(prev => ({ ...prev, [item.id]: e.target.value }))}
              className="bg-slate-950/50 border border-slate-700/50 focus:border-indigo-500  text-white text-sm rounded px-2 py-1 border  focus:outline-none focus:ring-1 focus:ring-blue-500 flex-1"
            >
              <option value="">— Select Manager —</option>
              {(Array.isArray(managers) ? managers : []).map((mgr: Manager) => (
                <option key={mgr.id} value={mgr.id}>
                  {mgr.name} ({mgr.email})
                </option>
              ))}
            </select>
            <button
              onClick={() => handleAssignManager(item.id)}
              disabled={assignStatus[item.id]?.loading}
              className="bg-green-600 hover:bg-green-700 px-2 py-1 rounded text-xs text-white disabled:opacity-50 cursor-pointer whitespace-nowrap transition-colors"
            >
              {assignStatus[item.id]?.loading ? "Saving…" : "Assign"}
            </button>
          </div>
          {assignStatus[item.id]?.message && (
            <p className={`text-xs ${assignStatus[item.id]?.type === "success" ? "text-green-400" : "text-red-400"}`}>
              {assignStatus[item.id].message}
            </p>
          )}
        </div>
      );
    } },
    { key: 'actions', label: 'Action', render: (_, item: Collaborator) => (
      <div className="flex flex-col gap-1.5 min-w-[200px]">
        <div className="flex gap-2">
          <button
            onClick={() => handleApproveAffiliate(item.id)}
            disabled={approveStatus[item.id]?.loading}
            className="bg-emerald-600 hover:bg-emerald-700 px-2.5 py-1.5 rounded text-xs text-white font-bold disabled:opacity-50 cursor-pointer transition-colors"
          >
            Approve
          </button>
          <button
            onClick={() => handleRejectAffiliate(item.id)}
            disabled={approveStatus[item.id]?.loading}
            className="bg-red-600 hover:bg-red-700 px-2.5 py-1.5 rounded text-xs text-white font-bold disabled:opacity-50 cursor-pointer transition-colors"
          >
            Reject
          </button>
        </div>
        {approveStatus[item.id]?.message && (
          <p className={`text-[10px] ${approveStatus[item.id]?.type === "success" ? "text-emerald-400" : "text-red-400"}`}>
            {approveStatus[item.id].message}
          </p>
        )}
      </div>
    ) }
  ];

  return (
    <div className="ml-0 lg:ml-[256px] p-6 text-white min-h-screen bg-[#151929]">

      <h2 className="text-xl sm:text-3xl font-bold mb-6 text-white">
        Pending Affiliates
      </h2>

      <div className="mb-6">
        <StakeTable
          isLoading={isLoadingAdmins}
          title=""
          columns={columns}
          data={admins}
          searchPlaceholder="Search by name or email..."
          onSearch={(val) => {
            setSearchTerm(val);
            setPage(1);
          }}
          emptyMessage={isLoadingAdmins ? "Loading pending affiliates..." : "No pending affiliates found"}
          hasSidebarMargin={false}
          page={page}
          totalPages={totalPages}
          total={total}
          limit={limit}
          onLimitChange={(newLimit) => {
            setLimit(newLimit);
            setPage(1);
          }}
          onPageChange={(newPage) => setPage(newPage)}
          actions={
            <button
              onClick={() => setShowModal(true)}
              className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded flex items-center transition-colors shadow-sm whitespace-nowrap text-sm"
            >
              + Create Affiliate
            </button>
          }
        />
      </div>



      {/* create modal */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title="Create Collaborator"
        width="max-w-md"
      >
        <p className="text-sm text-indigo-300 mb-4 bg-indigo-500/10 p-3 rounded-lg border border-indigo-500/20">
          Creating under: <span className="font-bold">{myProfile?.username}</span>
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <FormInput label="Username" name="username" value={formData.username} onChange={handleChange} required />
          <FormInput label="Email" type="email" name="email" value={formData.email} onChange={handleChange} required />
          <FormInput label="Password" type="password" name="password" value={formData.password} onChange={handleChange} required />
          
          <div className="pt-2 flex justify-end gap-3 border-t border-slate-800">
            <Button variant="ghost" type="button" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? "Creating..." : "Create"}
            </Button>
          </div>

          {message && (
            <div className={`mt-2 p-3 rounded text-sm text-center ${messageType === 'success' ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
              {message}
            </div>
          )}
        </form>
      </Modal>

      {/* profile modal */}
      <Modal
        open={profileModalVisible}
        onClose={() => setProfileModalVisible(false)}
        title="Collaborator Statistics"
        width="max-w-2xl"
        footer={
          <Button variant="ghost" onClick={() => setProfileModalVisible(false)}>
            Close
          </Button>
        }
      >
        {loadingProfile ? (
          <p className="text-center text-white my-8">Loading...</p>
        ) : selectedProfileDetails ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-white">
            <div className="bg-slate-950/50 border border-slate-700/50 focus:border-indigo-500  p-4 rounded">
              <p className="text-sm text-slate-400 mb-1">Total Users</p>
              <p className="text-2xl font-bold">{selectedProfileDetails.totalUser || 0}</p>
            </div>
            <div className="bg-slate-950/50 border border-slate-700/50 focus:border-indigo-500  p-4 rounded">
              <p className="text-sm text-slate-400 mb-1">Invited Collaborators</p>
              <p className="text-2xl font-bold">{selectedProfileDetails.invitedChild || 0}</p>
            </div>

            <div className="bg-slate-950/50 border border-slate-700/50 focus:border-indigo-500  p-4 rounded">
              <p className="text-sm text-slate-400 mb-1">Total Deposit</p>
              <p className="text-2xl font-bold text-green-400">₹{selectedProfileDetails.totalDeposit || 0}</p>
            </div>
            <div className="bg-slate-950/50 border border-slate-700/50 focus:border-indigo-500  p-4 rounded">
              <p className="text-sm text-slate-400 mb-1">Deposit Count</p>
              <p className="text-2xl font-bold text-green-400">{selectedProfileDetails.numberOfDepositCount || 0}</p>
            </div>
            <div className="bg-slate-950/50 border border-slate-700/50 focus:border-indigo-500  p-4 rounded sm:col-span-2">
              <p className="text-sm text-slate-400 mb-1">First Deposit Amount</p>
              <p className="text-2xl font-bold text-green-400">₹{selectedProfileDetails.firstDeposit || 0}</p>
            </div>

            <div className="bg-slate-950/50 border border-slate-700/50 focus:border-indigo-500  p-4 rounded">
              <p className="text-sm text-slate-400 mb-1">Total Withdraw</p>
              <p className="text-2xl font-bold text-red-400">₹{selectedProfileDetails.totalWithdraw || 0}</p>
            </div>
            <div className="bg-slate-950/50 border border-slate-700/50 focus:border-indigo-500  p-4 rounded">
              <p className="text-sm text-slate-400 mb-1">Withdraw Count</p>
              <p className="text-2xl font-bold text-red-400">{selectedProfileDetails.numberOfWithdrawCount || 0}</p>
            </div>
            <div className="bg-slate-950/50 border border-slate-700/50 focus:border-indigo-500  p-4 rounded sm:col-span-2">
              <p className="text-sm text-slate-400 mb-1">First Withdraw Amount</p>
              <p className="text-2xl font-bold text-red-400">₹{selectedProfileDetails.firstWithdraw || 0}</p>
            </div>
          </div>
        ) : (
          <p className="text-center text-red-400 my-8">Failed to load details.</p>
        )}
      </Modal>

    </div>
  );
};

export default PendingCollaboratorUser;